import {
  endOfMonth, addMonths, startOfMonth,
  endOfWeek,
  endOfYear,
  startOfWeek,
  startOfYear,
  addDays,
  addWeeks,
  addYears,
  isSameMonth,
  isSameYear,
  isValid,
} from 'date-fns';
import formatDate from '../../utils/formatDate';

export const INITIAL_RANGE = {
  key: 'dateRange',
};

/**
 * Trimester definitions mapping month ranges to start/end months
 * Non-standard quarters: T1(Jan-Mar), T2(Apr-Jun), T3(Jul-Aug), T4(Sep-Dec)
 */
const TRIMESTER_DEFINITIONS = [
  { start: 0, end: 2 }, // T1: January - March
  { start: 3, end: 5 }, // T2: April - June
  { start: 6, end: 7 }, // T3: July - August
  { start: 8, end: 11 }, // T4: September - December
];

/**
 * Number of months to display by type
 * @type {Record<ApiManagerDateRangeType, number>}
 */
export const NUMBER_MONTH_DISPLAY_BY_TYPE = {
  day: 6,
  week: 6,
  month: 6,
  quarter: 6,
  year: 12,
  custom: 6,
  all: 6,
};

/**
 * Get trimester definition for a given month
 * @param {number} month - Month index (0-11)
 * @returns {{start: number, end: number}} Trimester definition
 */
function getTrimesterDefinition(month) {
  return TRIMESTER_DEFINITIONS.find((def) => month >= def.start && month <= def.end)
    || TRIMESTER_DEFINITIONS[3]; // Default to T4 for safety
}

/**
 * Get trimester range based on picked date
 * Uses non-standard quarter definitions specific to business requirements
 * @param {Date} pickedDate - The date clicked by user
 * @returns {{startDate: Date, endDate: Date}} Trimester start and end dates
 */
export function getTrimesterRange(pickedDate) {
  if (!pickedDate) return { startDate: null, endDate: null };

  const year = pickedDate.getFullYear();
  const month = pickedDate.getMonth();
  const trimester = getTrimesterDefinition(month);

  const startDate = new Date(year, trimester.start, 1);
  const endDate = new Date(year, trimester.end + 1, 0); // Last day of end month

  return { startDate, endDate };
}

/**
 * Create today range
 * @param {Date} date - The date to create the range for
 * @returns {import('react-date-range').Range} Today's range
 */
export function createTodayRange(date = new Date()) {
  return {
    ...INITIAL_RANGE,
    startDate: date,
    endDate: date,
  };
}

/**
 * Create current week range
 * @param {Date} date - The date to create the range for
 * @returns {import('react-date-range').Range} Current week range
 */
export function createCurrentWeekRange(date = new Date()) {
  return {
    ...INITIAL_RANGE,
    startDate: startOfWeek(date),
    endDate: endOfWeek(date),
  };
}

/**
 * Create current month range
 * @param {Date} date - The date to create the range for
 * @returns {import('react-date-range').Range} Current month range
 */
export function createCurrentMonthRange(date = new Date()) {
  return {
    ...INITIAL_RANGE,
    startDate: startOfMonth(date),
    endDate: endOfMonth(date),
  };
}

/**
 * Create next 3 months range
 * @param {Date} date - The date to create the range for
 * @returns {import('react-date-range').Range} Next 3 months range
 */
export function createNext3MonthsRange(date = new Date()) {
  return {
    ...INITIAL_RANGE,
    startDate: startOfMonth(addMonths(date, 1)),
    endDate: endOfMonth(addMonths(date, 2)),
  };
}

/**
 * Create current quarter range
 * @param {Date} date - The date to create the range for
 * @returns {import('react-date-range').Range} Current quarter range
 */
export function createCurrentQuarterRange(date = new Date()) {
  const trimesterRange = getTrimesterRange(date);
  return {
    ...INITIAL_RANGE,
    startDate: trimesterRange.startDate,
    endDate: trimesterRange.endDate,
  };
}

/**
 * Create current year range
 * @param {Date} date - The date to create the range for
 * @returns {import('react-date-range').Range} Current year range
 */
export function createCurrentYearRange(date = new Date()) {
  return {
    ...INITIAL_RANGE,
    startDate: startOfYear(date),
    endDate: endOfYear(date),
  };
}

/**
 * Create date range based on span type and picked date
 * @param {Date} pickedDate - The picked date
 * @param {ApiManagerDateRangeType} type - The span type (day, week, month, quarter, year, custom)
 * @param {Date} [endDate] - End date for custom range
 * @returns {import('react-date-range').Range} Created range
 */
export function createRangeBySpan(pickedDate, type, endDate = null) {
  switch (type) {
    case 'day':
      return createTodayRange(pickedDate);
    case 'week':
      return createCurrentWeekRange(pickedDate);
    case 'month':
      return createCurrentMonthRange(pickedDate);
    case 'quarter':
      return createCurrentQuarterRange(pickedDate);
    case 'year':
      return createCurrentYearRange(pickedDate);
    case 'custom':
      return {
        ...INITIAL_RANGE,
        startDate: new Date(pickedDate),
        endDate: endDate ? new Date(endDate) : new Date(pickedDate),
      };
    default:
      throw new Error(`Unknown span type: ${type}`);
  }
}

/**
 * Get the next quarter based on the current start date and multiplier
 * @param {Date} currentStart - The current start date
 * @param {number} multiplier - The multiplier
 * @returns {Date} The next quarter
 */
export function getNextQuarter(currentStart, multiplier) {
  const currentQuarter = getTrimesterDefinition(currentStart.getMonth());
  const currentQuarterIndex = TRIMESTER_DEFINITIONS.findIndex((def) => def.start === currentQuarter.start && def.end === currentQuarter.end);

  // T4 to T3 (special case: only 2 months back)
  if (currentQuarterIndex === 3 && multiplier === -1) return addMonths(currentStart, -2);
  // T4 to T1 next year (4 months forward)
  if (currentQuarterIndex === 3 && multiplier === 1) return addMonths(currentStart, 4);
  // T3 to T4 (special case: only 2 months forward)
  if (currentQuarterIndex === 2 && multiplier === 1) return addMonths(currentStart, 2);
  // T1 to T4 previous year (4 months backward: Jan → Sep previous year)
  if (currentQuarterIndex === 0 && multiplier === -1) return addMonths(currentStart, -4);
  // Standard 3-month transitions: T1↔T2, T2↔T3 backward
  return addMonths(currentStart, multiplier * 3);
}

/**
 * Navigate to next or previous period based on current range and span
 * @param {import('react-date-range').Range} currentRange - Current active range
 * @param {DateRangeDirection} direction - Navigation direction
 * @param {string} type - Current span type
 * @returns {import('react-date-range').Range} New range or null if navigation not supported
 */
export function navigatePeriod(currentRange, direction, type) {
  if (!currentRange?.startDate) return null;

  const currentStart = currentRange.startDate;
  let multiplier = 0;
  switch (direction) {
    case 'plus':
      multiplier = 1;
      break;
    case 'minus':
      multiplier = -1;
      break;
    default:
      multiplier = 0;
      break;
  }

  switch (type) {
    case 'day':
      return createTodayRange(addDays(currentStart, multiplier));
    case 'week':
      return createCurrentWeekRange(addWeeks(currentStart, multiplier));
    case 'month':
      return createCurrentMonthRange(addMonths(currentStart, multiplier));
    case 'quarter': {
      const currentQuarterRange = getTrimesterRange(currentStart);
      const nextQuarterStart = getNextQuarter(currentQuarterRange.startDate, multiplier);
      return createCurrentQuarterRange(nextQuarterStart);
    }
    case 'year':
      return createCurrentYearRange(addYears(currentStart, multiplier));
    default:
      return currentRange;
  }
}

/**
 * Month delta between two dates (calendar months, not fixed 30-day periods)
 * @param {Date} from
 * @param {Date} to
 * @returns {number}
 */
function getMonthDelta(from, to) {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

/**
 * Navigate selectedDate forward or backward using the same calendar primitives as the span type
 * @param {Date} selectedDate - Current selected date
 * @param {DateRangeDirection} direction - Navigation direction
 * @param {ApiManagerDateRangeType} type - Current span type
 * @returns {Date | null} New selected date or null if navigation not supported
 */
export function navigateSelectedDate(selectedDate, direction, type) {
  if (!selectedDate) return null;

  let multiplier = 0;
  switch (direction) {
    case 'plus':
      multiplier = 1;
      break;
    case 'minus':
      multiplier = -1;
      break;
    default:
      return selectedDate;
  }

  switch (type) {
    case 'day':
      return addDays(selectedDate, multiplier);
    case 'week':
      return addWeeks(selectedDate, multiplier);
    case 'month':
      return addMonths(selectedDate, multiplier);
    case 'quarter': {
      const currentQuarterRange = getTrimesterRange(selectedDate);
      if (!currentQuarterRange.startDate) return null;
      const nextQuarterStart = getNextQuarter(currentQuarterRange.startDate, multiplier);
      const monthDelta = getMonthDelta(currentQuarterRange.startDate, nextQuarterStart);
      return addMonths(selectedDate, monthDelta);
    }
    case 'year':
      return addYears(selectedDate, multiplier);
    default:
      return selectedDate;
  }
}

/**
 * Format a week span from the received range bounds.
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {(key: string, options?: Record<string, string>) => string} t
 * @returns {string}
 */
function formatWeekRangeLabel(startDate, endDate, t) {
  if (isSameMonth(startDate, endDate) && isSameYear(startDate, endDate)) {
    return t('date.dateRangeWeekSameMonth', {
      startDay: formatDate(startDate, 'd', 'fr', ''),
      endDay: formatDate(endDate, 'd', 'fr', ''),
      monthYear: formatDate(endDate, 'MMM yyyy', 'fr', ''),
      week: formatDate(startDate, 'I', 'fr', ''),
    });
  }

  if (isSameYear(startDate, endDate)) {
    return t('date.dateRangeWeekAcrossMonths', {
      start: formatDate(startDate, 'd MMM', 'fr', ''),
      end: formatDate(endDate, 'd MMM', 'fr', ''),
      year: formatDate(endDate, 'yyyy', 'fr', ''),
    });
  }

  return t('date.dateRange', {
    start: formatDate(startDate, 'd MMM yyyy', 'fr', ''),
    end: formatDate(endDate, 'd MMM yyyy', 'fr', ''),
  });
}

/**
 * Format a quarter span from the received range bounds.
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {(key: string, options?: Record<string, string>) => string} t
 * @returns {string}
 */
function formatQuarterRangeLabel(startDate, endDate, t) {
  if (isSameYear(startDate, endDate)) {
    return t('date.dateRangeQuarter', {
      startMonth: formatDate(startDate, 'MMMM', 'fr', ''),
      endMonth: formatDate(endDate, 'MMMM', 'fr', ''),
      year: formatDate(endDate, 'yyyy', 'fr', ''),
    });
  }

  return t('date.dateRangeQuarterAcrossYears', {
    start: formatDate(startDate, 'MMMM yyyy', 'fr', ''),
    end: formatDate(endDate, 'MMMM yyyy', 'fr', ''),
  });
}

/**
 * Format the global filters date trigger label from the API range.
 * @param {object} params
 * @param {ApiManagerDateRangeType} [params.type]
 * @param {Date | null | undefined} params.startDate
 * @param {Date | null | undefined} params.endDate
 * @param {(key: string, options?: Record<string, string>) => string} t
 * @returns {string}
 */
export function formatGlobalDateRangeLabel({ type, startDate, endDate }, t) {
  if (!startDate || !endDate || !isValid(startDate) || !isValid(endDate)) return '';

  switch (type) {
    case 'day':
      return formatDate(startDate, 'EEEE d MMMM yyyy', 'fr', '');
    case 'month':
      return formatDate(startDate, 'MMMM yyyy', 'fr', '');
    case 'year':
      return formatDate(startDate, 'yyyy', 'fr', '');
    case 'quarter':
      return formatQuarterRangeLabel(startDate, endDate, t);
    case 'week':
      return formatWeekRangeLabel(startDate, endDate, t);
    case 'custom':
      return t('date.dateRange', {
        start: formatDate(startDate, 'eee d MMM yy', 'fr', ''),
        end: formatDate(endDate, 'eee d MMM yy', 'fr', ''),
      });
    case 'all':
      return t('date.dateRangeAll', {
        date: formatDate(startDate, 'd MMMM yyyy', 'fr', ''),
      });
    default:
      return t('date.dateRange', {
        start: formatDate(startDate, 'd MMM yyyy', 'fr', ''),
        end: formatDate(endDate, 'd MMM yyyy', 'fr', ''),
      });
  }
}
